import React, { createContext, useContext, useEffect, useMemo, useRef } from "react";

// =============================================================================
// UNSAVED GUARD
// =============================================================================
// A global "is any open form holding unsaved user input right now?" signal.
//
// WHY THIS EXISTS:
// The deploy-refresh poller (useVersionPoll) needs to decide whether it's safe
// to silently reload an open tab when a new deploy lands. Reloading a tab where
// a closer is mid-call typing a PostCallOutcome would wipe their work. Before
// this, there was NO global dirty signal in the app — the only `isDirty` lived
// inside GoalHeader — so the poller fired a blocking confirm() on every detect.
// This registry lets the poller ask hasUnsavedWork() instead.
//
// DESIGN — ref-counted, NOT useState (mirrors AuthContext's lastActivityRef):
// Forms call useUnsavedGuard(isDirty) on every render. If this wrote to React
// state, 16 forms toggling dirty on every keystroke would trigger a re-render
// storm app-wide. Instead each guard owns a unique Symbol id and adds/removes
// itself from a Set held in a ref. hasUnsavedWork() reads the Set size at
// decision time. Zero re-renders; the provider value is a stable object.
//
// A Set (not a counter) makes setDirty idempotent — registering the same id
// dirty twice can't double-count, and clearing a never-registered id is a no-op.
// =============================================================================

interface UnsavedGuardApi {
  /** Mark a source dirty (true) or clean (false). Keyed by a stable Symbol id. */
  setDirty: (id: symbol, dirty: boolean) => void;
  /** Remove a source entirely (called on unmount). */
  clear: (id: symbol) => void;
  /** True if ANY registered source currently reports unsaved input. */
  hasUnsavedWork: () => boolean;
}

const UnsavedGuardContext = createContext<UnsavedGuardApi | null>(null);

export function UnsavedGuardProvider({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  // The live set of dirty source ids. Ref so mutations never re-render.
  const dirtyRef = useRef<Set<symbol>>(new Set());

  // Stable api object (useMemo with [] = one identity for the provider's life).
  // Consumers' effects depend on this, so its identity must not churn. The
  // closures read dirtyRef.current at CALL time, never during render.
  const api = useMemo<UnsavedGuardApi>(
    () => ({
      setDirty: (id, dirty) => {
        if (dirty) dirtyRef.current.add(id);
        else dirtyRef.current.delete(id);
      },
      clear: (id) => {
        dirtyRef.current.delete(id);
      },
      hasUnsavedWork: () => dirtyRef.current.size > 0,
    }),
    []
  );

  return <UnsavedGuardContext.Provider value={api}>{children}</UnsavedGuardContext.Provider>;
}

/**
 * Register a form/modal's unsaved-input state with the global guard.
 *
 * Call this UNCONDITIONALLY at the top level of the component (Rules of Hooks),
 * BEFORE any early `return null` for closed modals. Pass `false` when the
 * surface is closed or pristine so it doesn't block a reload:
 *
 *   useUnsavedGuard(isOpen && notes.trim().length > 0);
 *
 * Safe outside a provider (returns inertly) so unit tests / Storybook don't crash.
 */
export function useUnsavedGuard(isDirty: boolean): void {
  const api = useContext(UnsavedGuardContext);
  const idRef = useRef<symbol | null>(null);
  if (idRef.current === null) idRef.current = Symbol("unsaved-guard");

  useEffect(() => {
    if (!api) return;
    const id = idRef.current as symbol;
    api.setDirty(id, isDirty);
    // Cleanup runs on isDirty change (re-set next pass) and on unmount (final
    // clear). Set semantics make the transient delete→add idempotent.
    return () => api.clear(id);
  }, [api, isDirty]);
}

/**
 * Read-side accessor for the poller. Returns the stable api (or null outside a
 * provider). Call api.hasUnsavedWork() at decision time — do NOT cache the
 * boolean, it must be read fresh each check.
 */
export function useUnsavedGuardController(): UnsavedGuardApi | null {
  return useContext(UnsavedGuardContext);
}
